import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Overlay } from '../_components/common';

// Fallback for when navigator.clipboard is unavailable or rejects (insecure context,
// older browser, no document focus). Selects a hidden textarea and asks the browser to copy.
const legacyCopy = (value) => {
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const CopyRow = ({ label, value }) => {
  const [state, setState] = useState('idle'); // 'idle' | 'copied' | 'failed'
  const copy = async () => {
    let ok = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) ok = legacyCopy(value);
    setState(ok ? 'copied' : 'failed');
    setTimeout(() => setState('idle'), ok ? 1500 : 4000);
  };
  const copied = state === 'copied';
  const failed = state === 'failed';
  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{label}</label>
      <div className="flex items-stretch gap-2">
        <code className="flex-1 px-3 py-2 rounded-lg bg-slate-900 text-slate-100 text-sm font-mono break-all select-all">{value}</code>
        <button type="button" onClick={copy} className="px-3 rounded-lg border border-slate-300 hover:bg-slate-100 text-slate-600 flex items-center gap-1 text-sm shrink-0">
          <Icon name={copied ? 'Check' : 'Copy'} size={15} />{copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {failed && (
        <p className="mt-1 text-xs text-amber-600 flex items-center gap-1">
          <Icon name="AlertTriangle" size={12} />Couldn’t copy — select the text manually.
        </p>
      )}
    </div>
  );
};

/**
 * Shown once, immediately after activation. Non-dismissable (no backdrop close, no X) until the
 * operator acknowledges they've stored the secret — because the secret is never retrievable again.
 */
const ActivationSecretModal = ({ creds, onAcknowledge }) => {
  const [ack, setAck] = useState(false);
  return (
    <Overlay onClose={undefined}>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-600"><Icon name="KeyRound" size={16} /></span>
          <h2 className="text-lg font-semibold text-slate-900">API activated — copy the secret now</h2>
        </div>
        <div className="px-5 py-4">
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 flex items-start gap-2">
            <Icon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
            <span>{creds.warning || 'This secret will not be shown again. Store it securely.'}</span>
          </div>
          <CopyRow label="API key (identifier)" value={creds.apiKey} />
          <CopyRow label="API secret (shown once)" value={creds.apiSecret} />
          <label className="flex items-start gap-2 mt-4 text-sm text-slate-600 cursor-pointer select-none">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
            I have copied and securely stored the secret.
          </label>
        </div>
        <div className="flex justify-end px-5 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <Button disabled={!ack} onClick={onAcknowledge}>Done</Button>
        </div>
      </div>
    </Overlay>
  );
};

export default ActivationSecretModal;
